import { useState } from 'react';
import type { RefObject } from 'react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

import toast from 'components/partials/toast/toast';

interface ExportToPdfArgs {
  insightName: string;
  contentRef: RefObject<HTMLDivElement | null>;
}

interface UseExportPdfResult {
  exportToPdf: (args: ExportToPdfArgs) => Promise<void>;
  isExporting: boolean;
}

export const useExportPdf = (): UseExportPdfResult => {
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const exportToPdf = async ({
    insightName,
    contentRef,
  }: ExportToPdfArgs): Promise<void> => {
    if (!contentRef || !contentRef.current) {
      toast.error({
        title: 'Export Failed',
        message: 'Content not found. Please try again.',
      });

      return;
    }

    setIsExporting(true);

    try {
      const element = contentRef.current;

      const originalOverflow = element.style.overflow;
      const originalMaxHeight = element.style.maxHeight;

      element.style.overflow = 'visible';
      element.style.maxHeight = 'none';

      const images = element.getElementsByTagName('img');

      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve();
              } else {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              }
            }),
        ),
      );

      const dataUrl = await toPng(element, {
        quality: 1,
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });

      element.style.overflow = originalOverflow;
      element.style.maxHeight = originalMaxHeight;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const img = new Image();

      img.src = dataUrl;

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (event) => {
          const message =
            typeof event === 'string' ? event : 'Failed to load image';

          reject(new Error(message));
        };
      });

      const imgWidth = img.width;
      const imgHeight = img.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgScaledWidth = imgWidth * ratio;
      const imgScaledHeight = imgHeight * ratio;

      let heightLeft = imgScaledHeight;
      let position = 0;

      pdf.addImage(
        dataUrl,
        'PNG',
        0,
        position,
        imgScaledWidth,
        imgScaledHeight,
        undefined,
        'FAST',
      );
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgScaledHeight;
        pdf.addPage();
        pdf.addImage(
          dataUrl,
          'PNG',
          0,
          position,
          imgScaledWidth,
          imgScaledHeight,
          undefined,
          'FAST',
        );
        heightLeft -= pdfHeight;
      }

      const fileName = `${insightName.replace(
        /[^a-z0-9]/gi,
        '_',
      )}_${new Date().getTime()}.pdf`;

      pdf.save(fileName);

      toast.success({
        title: 'PDF Downloaded',
        message: 'The insight has been successfully exported as PDF.',
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error({
        title: 'Export Failed',
        message:
          (error as Error)?.message ||
          'An error occurred while exporting the PDF.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportToPdf,
    isExporting,
  };
};
